<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

import CitySelectLoading from "#/components/header/CitySelectLoading.vue";
import CitySelectNoResult from "#/components/header/CitySelectNoResult.vue";

type City = {
  id: string;
  text: string;
};

const props = defineProps<{
  query: string;

  foundedCities: City[];

  selectedCity: City | null;

  isLoading: boolean;

  isPaginationLoading: boolean;

  hasSearched: boolean;
}>();

const emit = defineEmits<{
  (e: "update:query", value: string): void;

  (e: "select-city", city: City): void;

  (e: "submit"): void;

  (e: "scroll-dropdown", event: Event): void;

  (e: "focus"): void;
}>();

const root = ref<HTMLElement | null>(null);
const isDropdownVisible = ref<boolean>(false);
const isFocused = ref<boolean>(false);

const selectedCityLabel = computed<string>(() => {
  return props.selectedCity?.text ?? "";
});

const inputValue = computed<string>({
  get() {
    if (!isFocused.value && props.selectedCity) {
      return props.selectedCity.text;
    }

    return props.query;
  },

  set(value: string) {
    emit("update:query", value);
  },
});

const handleFocus = (): void => {
  isFocused.value = true;
  isDropdownVisible.value = true;
  emit("focus");
};

const handleBlur = (): void => {
  requestAnimationFrame(() => {
    setTimeout(() => {
      isFocused.value = false;

      isDropdownVisible.value = false;
    }, 120);
  });
};

const handleClickOutside = (event: MouseEvent): void => {
  const target = event.target as Node;
  if (!root.value?.contains(target)) {
    isDropdownVisible.value = false;
  }
};

const handleSelectCity = (city: City): void => {
  emit("select-city", city);
  isDropdownVisible.value = false;
};

onMounted(async () => {
  document.addEventListener("click", handleClickOutside);

  await nextTick();
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<template>
  <div ref="root" class="city-select">
    <div class="city-select__desktop">
      <div class="city-select__desktop-wrapper">
        <div class="city-select__desktop-input-wrapper">
          <input
            v-model="inputValue"
            type="text"
            :placeholder="!props.query && !selectedCityLabel ? 'Выберите город' : ''"
            class="city-select__input city-select__desktop-input ts-control"
            @focus="handleFocus"
            @blur="handleBlur"
          />

          <div
            class="city-select__desktop-arrow"
            :class="{
              'city-select__desktop-arrow--opened': isDropdownVisible,
            }"
          />

          <div
            v-if="isDropdownVisible"
            class="city-select__desktop-dropdown"
            @scroll="emit('scroll-dropdown', $event)"
          >
            <div v-if="props.isLoading" class="city-select__loader">
              <city-select-loading />
            </div>

            <template v-else-if="props.foundedCities.length">
              <div
                v-for="city in props.foundedCities"
                :key="city.id"
                class="city-select__item"
                @mousedown.stop="handleSelectCity(city)"
              >
                {{ city.text }}
              </div>

              <div v-if="props.isPaginationLoading" class="city-select__loader">
                <city-select-loading />
              </div>
            </template>

            <div v-else-if="props.hasSearched">
              <city-select-no-result />
            </div>
          </div>
        </div>

        <div class="city-select__desktop-submit">
          <button type="button" class="city-select__desktop-button btn" @click="emit('submit')">
            Выбрать
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.city-select {
  &__input {
    position: relative;
    z-index: 1;
    width: 100%;
    background: transparent;
    font-size: 16px;

    &::placeholder {
      font-size: 16px;
      color: #aeaeb9;
      font-family: Onest, sans-serif;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0;
    }
  }

  &__desktop {
    &-wrapper {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 8px;
    }

    &-input-wrapper {
      position: relative;
      width: 100%;
    }

    &-input {
      cursor: pointer;
      padding-right: 44px !important;

      &.ts-control {
        padding: 16px 18px;
      }
    }

    &-arrow {
      position: absolute;
      top: 50%;
      right: 16px;
      width: 16px;
      height: 16px;
      background: url("../img/icons/city-select-arrow.svg") no-repeat center;
      background-size: contain;
      transform: translateY(-50%);
      transition: transform 0.2s ease;
      pointer-events: none;
      z-index: 2;

      &--opened {
        transform: translateY(-50%) rotate(180deg);
      }
    }

    &-dropdown {
      position: absolute;
      top: calc(100% + 1px);
      left: 0;
      width: 100%;
      max-height: 280px;
      display: flex;
      flex-direction: column;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      overflow: hidden;
      overflow-y: auto;
      z-index: 10;
      box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.08),
        0 4px 6px -4px rgba(0, 0, 0, 0.08);

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 999px;

        &:hover {
          background: #9ca3af;
        }
      }
    }

    &-submit {
      button {
        height: 100%;
        padding: 14px 20px !important;
      }
    }
  }

  &__item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 20px;
    cursor: pointer;
    transition:
      background-color 0.15s ease,
      color 0.15s ease;

    &:hover {
      background: #f3f4f6;
    }

    &:not(:last-child) {
      border-bottom: 1px solid #f3f4f6;
    }
  }
}
</style>
